import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { EASE_OUT } from '@/lib/motion';
import { useSidebar } from '@/contexts/SidebarContext';
import { useProgramWizardState } from './useProgramWizardState';
import type { ProgramWizardInitialData } from './useProgramWizardState';
import type { ProgramWizardStepNumber } from './programWizardTypes';
import { PROGRAM_STEP_LABELS } from './programWizardTypes';
import { useProgram, useUpdateProgramStatus, useDeleteProgram } from '../../../services/programs';
import WizardStepIndicator from '../../courses/wizard/WizardStepIndicator';
import type { WizardStepNumber, WizardStepState } from '../../courses/wizard/wizardTypes';
import ProgramDetailsStep from './steps/ProgramDetailsStep';
import ProgramDaysStep from './steps/ProgramDaysStep';
import ProgramCertificationStep from './steps/ProgramCertificationStep';
import { useDismissible } from '@/hooks/learner/useMemberConfig';

// ─── Icons ───────────────────────────────────────────────────────────────────

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
    aria-hidden="true"
  >
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16 }}
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16 }}
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const SettingsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H7a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
  </svg>
);

const RocketIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
    aria-hidden="true"
  >
    <path d="M14 4l4 4-9 9-5 1 1-5 9-9z" />
    <path d="M5 19l-2 2" />
    <circle cx="13.5" cy="9.5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

const PenIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14 }}
    aria-hidden="true"
  >
    <path d="M15.232 5.232l3.536 3.536M16.707 3.293a2 2 0 012.828 0l1.172 1.172a2 2 0 010 2.828L7.5 20.5H3v-4.5L16.707 3.293z" />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    style={{ width: 14, height: 14 }}
    className="animate-spin"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
    <path d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

// ─── Bottom completion banner ────────────────────────────────────────────────

interface BottomReminderProps {
  programId: string;
}

function BottomReminder({ programId }: BottomReminderProps) {
  const navigate = useNavigate();
  const { dismissed, isLoading, dismiss } = useDismissible('program-wizard-completion-dismissed');

  return (
    <div className="bg-surface-primary rounded-2xl border border-[rgba(71,70,76,0.12)] dark:border-neutral-700 shadow-[0_4px_24px_rgba(71,70,76,0.08)] px-8 py-7">
      {/* Dismissible explanatory note. Only this collapses on "never show it
          again" — the action buttons below stay accessible regardless. */}
      <AnimatePresence initial={false}>
        {!isLoading && !dismissed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="overflow-hidden"
          >
            <div className="flex justify-between items-start mb-3">
              <span className="text-base font-semibold text-text-primary tracking-tight">
                Reminder
              </span>
              <button
                onClick={dismiss}
                className="text-[13px] font-medium text-primary-500 underline decoration-dotted hover:text-primary-600 dark:hover:text-primary-350 active:scale-[0.97] transition-[color,transform] duration-150"
              >
                Never show it again
              </button>
            </div>

            <p className="text-sm text-text-secondary leading-relaxed mb-7 max-w-[640px]">
              You just finished creating the program. In order to manage time frames, enroll
              audiences (learners) and launch it, go to Programs Library, find the program and click
              Configure.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-wrap items-center justify-center gap-8">
        <button
          onClick={() => navigate(`/programs/${programId}`)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold active:scale-[0.97] transition-[background-color,transform] duration-150"
        >
          <EyeIcon />
          Preview whole program
        </button>
        <button
          onClick={() => navigate('/programs?status=draft')}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary hover:opacity-90 text-white text-sm font-bold active:scale-[0.97] transition-[opacity,transform] duration-150"
        >
          Send to drafts in Programs Library
        </button>
        <button
          onClick={() => navigate(`/programs/${programId}/configure`)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-[0.7px] border-text-primary text-text-primary text-sm font-bold hover:bg-neutral-100 dark:hover:bg-neutral-800 active:scale-[0.97] transition-[background-color,transform] duration-150"
        >
          Send to drafts and configure right now
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Disabled overlay wrapper ─────────────────────────────────────────────────

interface DisabledWrapperProps {
  disabled: boolean;
  children: React.ReactNode;
}

function DisabledWrapper({ disabled, children }: DisabledWrapperProps) {
  if (!disabled) return <>{children}</>;
  return (
    <div className="relative">
      {children}
      <div className="absolute inset-0 rounded-2xl bg-surface-primary/70 backdrop-blur-sm cursor-not-allowed z-10 flex items-center justify-center">
        <span className="text-[13px] font-medium text-text-secondary bg-surface-elevated border border-border-primary/20 rounded-lg px-3.5 py-1.5 shadow-sm">
          Complete Step 1 first
        </span>
      </div>
    </div>
  );
}

function LockedStepCard({ number, label, description }: { number: number; label: string; description: string }) {
  return (
    <div className="bg-surface-primary rounded-2xl border border-[rgba(71,70,76,0.1)] dark:border-neutral-700 p-7 min-h-[100px] flex items-center gap-4">
      <div className="w-9 h-9 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-sm font-bold text-neutral-400 shrink-0">
        {number}
      </div>
      <div>
        <div className="text-[15px] font-semibold text-neutral-400 mb-1">{label}</div>
        <div className="text-[13px] text-neutral-300 dark:text-neutral-600">{description}</div>
      </div>
    </div>
  );
}

// ─── Main wizard ─────────────────────────────────────────────────────────────

interface ProgramCreationWizardProps {
  /** Pass initial data for edit mode. If omitted, wizard runs in create mode. */
  initialData?: ProgramWizardInitialData;
}

export default function ProgramCreationWizard({ initialData }: ProgramCreationWizardProps) {
  const navigate = useNavigate();
  const { setCollapsed: setSidebarCollapsed } = useSidebar();
  const { state, setProgramId, markStepSaved, isStepEnabled, allStepsSaved } =
    useProgramWizardState(initialData);
  const reduceMotion = useReducedMotion();
  const stepDelay = (i: number) => (reduceMotion ? 0 : i * 0.06);

  // Edit-mode publish controls
  const { data: programData } = useProgram(state.programId ?? undefined);
  const updateProgramStatus = useUpdateProgramStatus();
  const deleteProgram = useDeleteProgram();
  const [discarding, setDiscarding] = useState(false);

  // Change-of-mind escape hatch (create mode) — mirrors the course wizard: a
  // draft exists once step 1 saves, so backing out must offer deleting it.
  const handleDiscard = async () => {
    if (!window.confirm('Delete this program and everything you added to it? This cannot be undone.')) {
      return;
    }
    setDiscarding(true);
    try {
      if (state.programId) await deleteProgram.mutateAsync(state.programId);
      toast('Program discarded', { icon: '🗑️' });
      navigate('/programs');
    } catch {
      toast.error('Could not delete the program. Please try again.');
      setDiscarding(false);
    }
  };
  const programStatus = programData?.status;
  const isActive = programStatus === 'active';

  const setStatus = useCallback(
    async (next: 'active' | 'draft' | 'archived') => {
      if (!state.programId) return;
      try {
        await updateProgramStatus.mutateAsync({ programId: state.programId, status: next });
        toast.success(
          next === 'active'
            ? 'Program published — visible to learners.'
            : next === 'draft'
              ? 'Program moved to drafts — hidden from learners.'
              : 'Program hidden from learners.'
        );
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Could not update program status.';
        toast.error(message);
      }
    },
    [state.programId, updateProgramStatus]
  );

  // Auto-collapse sidebar when entering the wizard
  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  // Center-of-viewport focus tracking
  const [focusedStep, setFocusedStep] = useState<ProgramWizardStepNumber>(1);
  const stepRefs = useRef<Record<ProgramWizardStepNumber, HTMLDivElement | null>>({
    1: null,
    2: null,
    3: null,
  });
  const setStepRef = useCallback(
    (step: ProgramWizardStepNumber) => (el: HTMLDivElement | null) => {
      stepRefs.current[step] = el;
    },
    []
  );

  useEffect(() => {
    const handleScroll = () => {
      const refs = stepRefs.current;
      const viewportCenter = window.innerHeight / 2;
      let best: ProgramWizardStepNumber = 1;
      let bestDistance = Infinity;
      for (const step of [1, 2, 3] as ProgramWizardStepNumber[]) {
        const el = refs[step];
        if (!el) continue;
        const { top, bottom } = el.getBoundingClientRect();
        const stepCenter = (top + bottom) / 2;
        const distance = Math.abs(stepCenter - viewportCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = step;
        }
      }
      setFocusedStep(best);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [state.programId]);

  // Step 1 saved
  const handleStep1Saved = (programId: string) => {
    setProgramId(programId);
    markStepSaved(1);
  };

  const step2Enabled = isStepEnabled(2);
  const step3Enabled = isStepEnabled(3);

  // The shared WizardStepIndicator is parameterized over 1|2|3|4 but accepts a
  // `labels` map and a 3-step layout when `gamificationEnabled=false`. We
  // borrow that 3-step layout for programs by coercing our 1-3 step state into
  // the 1-2-4 slots the indicator expects (skip slot 3, treat our "step 3" as
  // slot 4) — this keeps the visual identical to the courses indicator without
  // forking the component.
  const indicatorSteps: Record<WizardStepNumber, WizardStepState> = {
    1: state.steps[1],
    2: state.steps[2],
    3: { status: 'pending' }, // hidden when gamificationEnabled=false
    4: state.steps[3],
  };
  const indicatorLabels: Record<WizardStepNumber, string> = {
    1: PROGRAM_STEP_LABELS[1],
    2: PROGRAM_STEP_LABELS[2],
    3: '', // hidden
    4: PROGRAM_STEP_LABELS[3],
  };
  const indicatorFocused: WizardStepNumber =
    focusedStep === 1 ? 1 : focusedStep === 2 ? 2 : 4;

  return (
    <div className="min-h-screen">
      <Toaster position="top-right" />

      <WizardStepIndicator
        steps={indicatorSteps}
        focusedStep={indicatorFocused}
        gamificationEnabled={false}
        labels={indicatorLabels}
      />

      {/* Edit-mode toolbar: status controls + jump to /configure */}
      {initialData && state.programId && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : 0.08 }}
          className="max-w-[1080px] mx-auto px-6 pt-6 flex items-center justify-end gap-2"
        >
          {programStatus && !isActive && (
            <button
              type="button"
              onClick={() => setStatus('active')}
              disabled={updateProgramStatus.isPending}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-white bg-success-500 hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {updateProgramStatus.isPending ? <SpinnerIcon /> : <RocketIcon />}
              Publish
            </button>
          )}

          {isActive && (
            <button
              type="button"
              onClick={() => setStatus('draft')}
              disabled={updateProgramStatus.isPending}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-primary border border-text-primary/30 hover:bg-text-primary/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {updateProgramStatus.isPending ? <SpinnerIcon /> : <PenIcon />}
              Set to draft
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate(`/programs/${state.programId}/configure`)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-white hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150"
            style={{ background: `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)` }}
          >
            <SettingsIcon />
            Configure
          </button>
        </motion.div>
      )}

      {/* Create-mode toolbar: both ways out are explicit — keep the draft, or
          delete it and return to the library. */}
      {!initialData && (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : 0.08 }}
          className="max-w-[1080px] mx-auto px-6 pt-6 flex items-center justify-between gap-2"
        >
          <button
            type="button"
            onClick={() => navigate('/programs')}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-grey-900 border border-grey-900/30 dark:text-text-primary dark:border-text-primary/30 hover:bg-grey-900/[0.06] dark:hover:bg-text-primary/[0.08] active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            {state.programId ? '← Keep draft & exit' : '← Back to Programs'}
          </button>

          {state.programId && (
            <button
              type="button"
              onClick={handleDiscard}
              disabled={discarding}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-error-600 dark:text-error-400 border border-error-500/40 hover:bg-error-500/[0.07] active:scale-[0.97] transition-[background-color,transform] duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {discarding ? <SpinnerIcon /> : <TrashIcon />}
              Discard program
            </button>
          )}
        </motion.div>
      )}

      <div className="max-w-[1080px] mx-auto px-6 pt-8 pb-20 flex flex-col gap-10">
        {/* Step 1: Basics */}
        <div ref={setStepRef(1)} data-wizard-step="1">
          <ProgramDetailsStep
            programId={state.programId}
            initialData={
              initialData
                ? {
                    name: initialData.name ?? '',
                    description: initialData.description ?? null,
                    header_image: initialData.header_image ?? null,
                  }
                : undefined
            }
            onSaved={handleStep1Saved}
            onCancel={() => navigate(initialData ? `/programs/${state.programId}` : '/programs')}
            entranceDelay={stepDelay(0)}
          />
        </div>

        {/* Step 2: Content */}
        <div ref={setStepRef(2)} data-wizard-step="2">
          <DisabledWrapper disabled={!step2Enabled}>
            {state.programId ? (
              <ProgramDaysStep
                programId={state.programId}
                disabled={!step2Enabled}
                onSaved={() => markStepSaved(2)}
              />
            ) : (
              <LockedStepCard
                number={2}
                label="Content"
                description="Complete Step 1 to unlock day-and-course planning."
              />
            )}
          </DisabledWrapper>
        </div>

        {/* Step 3: Certification */}
        <div ref={setStepRef(3)} data-wizard-step="3">
          <DisabledWrapper disabled={!step3Enabled}>
            {state.programId ? (
              <ProgramCertificationStep
                programId={state.programId}
                initialCertificationType={initialData?.certification_type ?? null}
                initialCertificateFileKey={initialData?.certificate_file_key ?? null}
                disabled={!step3Enabled}
                onSaved={() => markStepSaved(3)}
                entranceDelay={stepDelay(2)}
              />
            ) : (
              <LockedStepCard
                number={3}
                label="Certification"
                description="Complete Step 1 to unlock certification settings."
              />
            )}
          </DisabledWrapper>
        </div>

        {/* Bottom completion banner — visible only when all 3 steps are saved */}
        {allStepsSaved && state.programId && <BottomReminder programId={state.programId} />}
      </div>
    </div>
  );
}
